import React, { useEffect, useState, useRef } from "react";
import "./index.css";

/**
 * StickyHeader - A reusable component that sticks to the top of the page when scrolling
 * @param {React.ReactNode} children - Content to display in the header
 * @param {string} className - Additional CSS classes
 * @param {number} offset - Offset from top when sticky (default: 0)
 * @param {boolean} alwaysSticky - If true, header is always sticky (default: false)
 * @param {number} unstickyThreshold - Distance from top (in px) where header becomes unsticky (default: 50)
 * @param {object} style - Additional inline styles
 */
const StickyHeader = ({ 
  children, 
  className = "", 
  offset = 0, 
  alwaysSticky = false,
  unstickyThreshold = 50,
  style = {},
  ...props 
}) => {
  const [isSticky, setIsSticky] = useState(alwaysSticky);
  const headerRef = useRef(null);
  const placeholderRef = useRef(null);
  const originalTopRef = useRef(null);

  useEffect(() => {
    if (alwaysSticky) {
      setIsSticky(true);
      return;
    }

    // Calculate and store the original top position of the header
    const calculateOriginalPosition = () => {
      if (headerRef.current && originalTopRef.current === null) {
        // Get position relative to document
        const rect = headerRef.current.getBoundingClientRect();
        originalTopRef.current = rect.top + window.scrollY;
      }
    };

    // Calculate original position after DOM is ready
    const timeoutId = setTimeout(calculateOriginalPosition, 0);

    const handleScroll = () => {
      if (originalTopRef.current !== null) {
        const scrollY = window.scrollY;
        // If scrollY is within threshold of 0, always set to unsticky
        if (scrollY <= unstickyThreshold) {
          setIsSticky(false);
        } else {
          // Otherwise, check if we've scrolled past the original position
          const shouldBeSticky = scrollY >= (originalTopRef.current - offset);
          setIsSticky(shouldBeSticky);
        }
      } else {
        // If original position not set yet, calculate it
        calculateOriginalPosition();
      }
    };

    // Recalculate original position on resize
    const handleResize = () => {
      if (headerRef.current) {
        // Force recalculation by resetting the ref
        originalTopRef.current = null;
        calculateOriginalPosition();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });
    handleScroll(); // Check initial state

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [offset, alwaysSticky, unstickyThreshold]);

  useEffect(() => {
    // Update placeholder height when sticky state changes
    if (placeholderRef.current && headerRef.current) {
      if (isSticky) {
        placeholderRef.current.style.height = `${headerRef.current.offsetHeight + 32}px`;
      } else {
        placeholderRef.current.style.height = "0px";
      }
    }
  }, [isSticky]);

  return (
    <>
      <div ref={placeholderRef} className="sticky-header-placeholder" />
      <header
        ref={headerRef}
        className={`sticky-header ${isSticky ? "sticky" : ""} ${className}`}
        style={{ top: `${offset}px`, ...style }}
        {...props}
      >
        {children}
      </header>
    </>
  );
};

export default StickyHeader;
